/** Format a numeric rating to one decimal place, e.g. 7.8 → "7.8" */
export function formatRating(val: number | undefined): string {
  return val != null ? val.toFixed(1) : '';
}

/** Extract the 4-digit year from a date string, e.g. "2024-05-01" → "2024" */
export function releaseYear(date: string | undefined): string {
  return date ? date.slice(0, 4) : '';
}

/**
 * Rotten Tomatoes emoji: 🍅 for ≥60%, 🍏 for <60%.
 * Handles both 0-1 (fractional) and 0-100 (percentage) scales.
 */
export function rtEmoji(val: number | undefined): string {
  if (val == null) return '';
  return (val <= 1 ? val * 100 : val) >= 60 ? '🍅' : '🍏';
}

/**
 * Format a rating as an integer percentage string.
 * Handles both 0-1 (fractional) and 0-100 (percentage) scales.
 */
export function formatPct(val: number | undefined): string {
  if (val == null) return '';
  return Math.round(val * (val <= 1 ? 100 : 1)).toString();
}

/**
 * Check whether any standard rating field is present.
 * Works with any object that has the common rating fields.
 */
export function hasRatings(d: {
  imdbRating?: number;
  rtRatingCritic?: number;
  rtRatingAudience?: number;
  metacriticRating?: number;
}): boolean {
  return !!(
    d.imdbRating ??
    d.rtRatingCritic ??
    d.rtRatingAudience ??
    d.metacriticRating
  );
}

/** Join an optional string array with commas */
export function joinList(items: string[] | undefined): string {
  return (items ?? []).join(', ');
}

/**
 * Price level symbol (€ repeated 1-4 times).
 * Handles both numeric and string levels.
 */
export function priceSymbol(level: string | number | undefined): string {
  if (!level) return '';
  const n = typeof level === 'number' ? level : parseInt(String(level), 10);
  return '€'.repeat(Math.max(1, Math.min(n, 4)));
}

export function truncate(
  value: string | number | boolean | null | undefined,
  maxLength: number
): string {
  const text = String(value ?? '');
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

export function flagUrl(code: string | undefined): string | null {
  if (!code || code.length !== 2) return null;
  return `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
}

export function flagUrl2x(code: string | undefined): string | null {
  if (!code || code.length !== 2) return null;
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
}

/**
 * Format duration in seconds to human-readable digital clock format.
 * Produces formats like "1:23:45" for durations with hours, "45:30" for shorter durations.
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "1:23:45" or "45:30")
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || Number.isNaN(seconds) || seconds < 0) {
    return '0:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(secs)}`;
  }
  return `${minutes}:${pad(secs)}`;
}

/**
 * Format duration in milliseconds to human-readable digital clock format.
 * Produces formats like "1:23:45" for durations with hours, "45:30" for shorter durations.
 * Returns '—' for null/undefined values.
 *
 * @param milliseconds - Duration in milliseconds
 * @returns Formatted duration string (e.g., "1:23:45" or "45:30"), or '—' for null
 */
export function formatDurationMs(
  milliseconds: number | null | undefined
): string {
  if (milliseconds == null) return '—';
  if (
    typeof milliseconds !== 'number' ||
    Number.isNaN(milliseconds) ||
    milliseconds < 0
  ) {
    return '0:00';
  }
  return formatDuration(Math.floor(milliseconds / 1000));
}

/**
 * Format duration in milliseconds to short format for timing/benchmarks.
 * Produces formats like "XXXms" for sub-second durations, "X.Xs" for >= 1 second.
 *
 * @param milliseconds - Duration in milliseconds
 * @returns Formatted duration string (e.g., "500ms", "1.5s"), or '—' for null
 */
export function formatDurationShort(
  milliseconds: number | null | undefined
): string {
  if (milliseconds == null) return '—';
  if (
    typeof milliseconds !== 'number' ||
    Number.isNaN(milliseconds) ||
    milliseconds < 0
  ) {
    return '—';
  }

  const seconds = milliseconds / 1000;

  // Handle zero explicitly - show as seconds with decimal
  if (seconds === 0) {
    return '0.0s';
  }

  // If less than 1 second, show in milliseconds
  if (seconds < 1) {
    return `${Math.round(seconds * 1000)}ms`;
  }

  // If less than 10 seconds, show with decimal precision
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }

  // For 10+ seconds, show as whole seconds
  return `${Math.round(seconds)}s`;
}

/**
 * Format duration in milliseconds to human-readable format.
 * Produces formats like "Xm XXs" for durations with minutes, "XXs" for shorter durations.
 *
 * @param milliseconds - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2m 5s", "30s")
 */
export function formatDurationHuman(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}
